import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useTranslation } from 'react-i18next';

const ENDPOINT_TEMPLATE = '/profile/set-primary/{uuid}';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_TEMPLATE}` as const;
type Url = typeof ENDPOINT_URL_WITH_VERSION;

export type SetPrimaryVehicleInput = paths[Url]['put'];

export interface SetPrimaryVehicleSuccessResponse {
    message: string;
}

export const useSetPrimaryVehicle = () => {
    const { t } = useTranslation();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const request = async ({ uuid }: { uuid: string }) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const endpointUrl = ENDPOINT_TEMPLATE.replace('{uuid}', uuid);

        try {
            const inputParameters = new URLSearchParams(getCommonParameters()).toString();

            const response = await fetch(`${API_URL}${endpointUrl}?${inputParameters}`, {
                method: 'PUT',
                headers: getHeaders(),
            });

            const contentType = response.headers.get('content-type');

            const data: SetPrimaryVehicleSuccessResponse = contentType?.includes('application/json')
                ? await response.json()
                : { message: t('common.success') };

            if (!response.ok) {
                const apiError = new Error(data.message || t('common.error-occurred')) as ApiError;
                apiError.statusCode = response.status;
                throw apiError;
            }

            const successMessage = data.message || t('profile.primary-vehicle-set-success');
            setSuccess(successMessage);
            return successMessage;
        } catch (err: any) {
            handleCommonErrors(err);
            err?.message && setError(err.message);
            throw err;
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime, success };
};
